import React, { useMemo } from 'react';

import { Box } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { token } from '@betterment/samba-styled-system/tokens';
import { AxisBottom } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { Bar, Line, LinePath } from '@visx/shape';
import { timeFormat } from 'd3-time-format';

import { DirectTradingAccountSecurityPricePerformanceQuery } from './graphql/directTradingAccountSecurityPricePerformance.generated';

import { useXYChartTooltipInPortal } from '../../../../../Shared/components-v2/charts/components/XYChartTooltip';
import {
  IndexDomainScale,
  useChartHighlight,
} from '../../../../../Shared/components-v2/charts/hooks/useChartHighlight';
import { useTickLabelProps } from '../../../../../Shared/components-v2/charts/hooks/useTickLabelProps';

type PricePointType =
  DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['pricePerformance']['timeSeries'][number];

type PerformanceChartProps = {
  data: PricePointType[];
  height: number;
  width: number;
  disabled?: boolean;
  onHighlightChange: (highlight: PricePointType | null) => void;
};

const defaultMargin = { top: 10, right: 1, bottom: 20, left: 1 };

const formatDate = timeFormat('%b %d, %Y');

export const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data: remoteData,
  width,
  height,
  disabled = false,
  onHighlightChange,
}) => {
  const data = useMemo(() => remoteData.map(d => ({ ...d, dateTime: new Date(d.dateTime) })), [remoteData]); // Ensure dateTime is a Date object
  const xMax = Math.max(0, width - defaultMargin.left - defaultMargin.right);
  const yMax = Math.max(0, height - defaultMargin.top - defaultMargin.bottom);

  const performanceScale = useMemo(() => {
    const prices = data.map(d => d.amount.cents);
    const [min, max] = [Math.min(...prices), Math.max(...prices)];
    const average = (max + min) / 2;
    const offset = ((max - min) / 2) * 1.03;
    return scaleLinear({ range: [yMax, 0], domain: [average - offset, average + offset], nice: true });
  }, [yMax, data]);

  const dateScale = useMemo(() => scaleLinear({ range: [0, xMax], domain: [0, data.length - 1] }), [xMax, data.length]);

  const chartHighlight = useChartHighlight<PricePointType, IndexDomainScale>({
    dateScale,
    data,
    xAccessor: d => data.indexOf(d),
    disabled,
    marginLeft: defaultMargin.left,
    onHighlightChange,
  });
  const { highlightData, handleHighlightUpdate, handleHighlightCancel } = chartHighlight;

  const { XYChartTooltipInPortal, containerRef } = useXYChartTooltipInPortal();
  const tickLabelProps = useTickLabelProps({ highlightData });

  return (
    <Box css={{ position: 'relative', width: '100%', maxWidth: width }}>
      <svg width={width} height={height} ref={containerRef} style={{ maxWidth: '100%' }}>
        <rect x={0} y={0} width={width} height={height} fill={token('colors.background.surface')} />
        <Group left={defaultMargin.left} top={defaultMargin.top}>
          <GridRows
            scale={performanceScale}
            width={xMax}
            height={yMax}
            strokeDasharray="4,4"
            stroke={token('colors.border.weak')}
            pointerEvents="none"
            numTicks={5}
          />
          <AxisBottom
            scale={dateScale}
            top={yMax}
            tickValues={[0, data.length - 1]}
            tickFormat={(v, i) => (i === 1 ? 'Today' : formatDate(data[v.valueOf()].dateTime))}
            hideTicks={true}
            rangePadding={0}
            labelOffset={0}
            axisLineClassName={css({ stroke: 'border' })}
            tickLabelProps={tickLabelProps}
          />
          {/*  Amount line */}
          <LinePath
            className={css({ stroke: 'dataviz.blue.2', strokeWidth: '2px' })}
            data={data}
            strokeLinejoin="round"
            x={(_, i) => dateScale(i)}
            y={d => performanceScale(d.amount.cents)}
          />
          <Bar
            x={0}
            y={0}
            width={xMax}
            height={yMax}
            fill="transparent"
            onTouchStart={handleHighlightUpdate}
            onTouchMove={handleHighlightUpdate}
            onMouseMove={handleHighlightUpdate}
            onMouseLeave={handleHighlightCancel}
          />
          {highlightData && (
            <g>
              <Line
                from={{ x: highlightData.highlightLeft, y: -10 }}
                to={{ x: highlightData.highlightLeft, y: height - defaultMargin.bottom - defaultMargin.top }}
                stroke={token('colors.border.strongest')}
                strokeWidth={1}
                pointerEvents="none"
              />
              <circle
                cx={highlightData.highlightLeft}
                cy={height - defaultMargin.bottom - defaultMargin.top}
                r={1.5}
                fill={token('colors.border.strongest')}
                stroke={token('colors.border.strongest')}
                strokeWidth={2}
                pointerEvents="none"
              />
            </g>
          )}
        </Group>
      </svg>
      {highlightData && (
        <Box>
          <XYChartTooltipInPortal tooltipLeft={highlightData.highlightLeft} chartHeight={height} margin={defaultMargin}>
            {formatDate(new Date(highlightData.highlight?.dateTime))}
          </XYChartTooltipInPortal>
        </Box>
      )}
    </Box>
  );
};
